// CRM-iframe chat-selection reporter.
//
// The CRM inbox renders a right sidebar with the lead behind the currently
// open chat. telegram-tt is the only place that knows which chat the operator
// has selected, so it forwards that selection to the parent SPA via
// postMessage (`tg:chatSelected`). The SPA looks the peer up as a CRM contact
// and swaps the sidebar's card.
//
// Unlike crmInboundReporter (which hooks `apiUpdate`/newMessage), chat
// selection is pure UI navigation — no apiUpdate fires when you just click a
// dialog. So we subscribe to the global state via teactn's addCallback and
// diff the current message-list chatId on each change.
//
// Lives in its own module (not crmBridge.ts) because crmBridge is imported by
// the gramjs worker too, where there is no global store / window. This file is
// imported only from index.tsx on the main thread.

import { getGlobal } from '../global';
import { getMainUsername } from '../global/helpers/users';
import { selectCurrentMessageList } from '../global/selectors';
import { addCallback } from '../lib/teact/teactn';

import { getCrmAccountId, getCrmOrigin, isCrmIframeMode } from './crmBridge';
import { isUserId } from './entities/ids';

let isRegistered = false;
// `undefined` = nothing reported yet; `null` = explicitly reported "no/empty
// selection". Distinguishing the two avoids posting a redundant null on init.
let lastReportedChatId: string | null | undefined;

function postToCrm(payload: object) {
  const origin = getCrmOrigin();
  if (!origin || !window.parent || window.parent === window) return;
  try {
    window.parent.postMessage(payload, origin);
  } catch {
    // Best effort — never block the update pipeline on telemetry.
  }
}

export function registerCrmChatSelectReporter() {
  if (isRegistered || !isCrmIframeMode()) return;
  isRegistered = true;

  addCallback((global: ReturnType<typeof getGlobal>) => {
    // Best-effort: a throw here would propagate into teactn's callback drain
    // and break other subscribers in the tick — so swallow everything.
    try {
      const chatId = selectCurrentMessageList(global)?.chatId ?? null;
      if (chatId === lastReportedChatId) return;
      lastReportedChatId = chatId;

      // No chat open, or a non-DM (group/channel/Saved Messages): the sidebar
      // only shows 1:1 leads, so report an empty selection and let the SPA
      // render its placeholder.
      if (!chatId || !isUserId(chatId) || chatId === global.currentUserId) {
        postToCrm({
          type: 'tg:chatSelected',
          payload: { accountId: getCrmAccountId(), peerId: null, kind: chatId ? 'non-dm' : 'none' },
        });
        return;
      }

      const peer = global.users.byId[chatId];
      postToCrm({
        type: 'tg:chatSelected',
        payload: {
          accountId: getCrmAccountId(),
          peerId: chatId,
          username: peer ? getMainUsername(peer) : undefined,
          firstName: peer?.firstName,
          lastName: peer?.lastName,
          phone: peer?.phoneNumber,
        },
      });
    } catch {
      // never disrupt the state-callback pipeline
    }
  });
}
